import { useCallback, useEffect, useState } from 'react';

import { doRequest } from '@r1-frontend/do-request';
import { getMerchantInfo, IGetMerchantInfoResponseDto } from '@r1-frontend/api-domru/api-profile/v1/pay-list/acquiring-seamless-pay-gw-list';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { fetchAuthorizeApplePaySession } from '~/src/api/authorizeApplePaySession';
import { IS_PRODUCTION_ENVIROMENT } from '~/src/constants';
import { loadScript } from '~/src/helpers/load-script';

import * as ST from './styled';

export const TYPE_APPLE_TOKEN = '2';

const APPLE_PAY_BTN_SCRIPT = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';

interface IMerchantInfo {
    gateway: string,
    gatewayMerchantId: string,
    samsungService: string,
    merchantId: string,
}

const getAppleConf = (info: IMerchantInfo) => ([{
    supportedMethods: 'https://apple.com/apple-pay',
    data: {
        environment: IS_PRODUCTION_ENVIROMENT ? 'PRODUCTION' : 'TEST',
        version: 3,
        merchantIdentifier: IS_PRODUCTION_ENVIROMENT ? info.merchantId : 'example.com.store',
        merchantCapabilities: [
            'supports3DS',
        ],
        supportedNetworks: [
            'masterCard',
            'visa',
        ],
        countryCode: 'RU',
    },
}]);

interface IProps {
    currentCity: ICurrentCity,
    paySumm: number,
    onPaymentCreate: (payToken: string, type: typeof TYPE_APPLE_TOKEN) => Promise<boolean | undefined>,
}

export const ApplePayBtn = ({ currentCity, paySumm, onPaymentCreate }: IProps): JSX.Element => {
    const [isLoadedScript, setIsLoadedScript] = useState(false);

    const [paymentMethodData, setPaymentMethodData] = useState<PaymentMethodData[] | null>(null);

    const onPayClick = useCallback(async() => {
        if (paymentMethodData) {
            const details = {
                total: { label: 'dom.ru', amount: { currency: 'RUB', value: `${paySumm}` } },
            };

            const request = new PaymentRequest(paymentMethodData, details);

            // @ts-ignore
            request.onmerchantvalidation = async(event) => {
                const appleSession = await fetchAuthorizeApplePaySession(currentCity.domain);

                if (appleSession.isSuccess) {
                    event.complete(appleSession.payload);
                }
            };

            const paymentResp = await request.show();

            const payToken = paymentResp?.details?.token?.paymentData; // apple токен

            const payResult = await onPaymentCreate(payToken || '', TYPE_APPLE_TOKEN);

            paymentResp.complete(payResult ? 'success' : 'fail');
        }
    }, [paymentMethodData, paySumm, onPaymentCreate, currentCity]);

    const initialazeClient = useCallback(async() => {
        if (!isLoadedScript) {
            try {
                await loadScript(APPLE_PAY_BTN_SCRIPT);
                setIsLoadedScript(true);
            } catch {
                return;
            }
        }

        const resp = await doRequest.apiProfile<IGetMerchantInfoResponseDto>(getMerchantInfo());

        if (!resp.isSuccess) {
            return;
        }

        const methodData = getAppleConf(resp.payload);

        /* Проверка доступности оплаты, с помощью ApplePay */
        const details = {
            total: { label: 'Test purchase', amount: { currency: 'RUB', value: '0.01' } },
        };

        const request = new PaymentRequest(methodData, details);

        const canMakePayment = await request.canMakePayment();

        if (canMakePayment) {
            setPaymentMethodData(methodData);
        }
    }, [isLoadedScript]);

    useEffect(() => {
        initialazeClient();
    }, [initialazeClient]);

    useEffect(() => {
        if (isLoadedScript) {
            const appleBtn = document.querySelector('apple-pay-button');

            appleBtn?.addEventListener('click', onPayClick);

            return () => {
                appleBtn?.removeEventListener('click', onPayClick);
            };
        }
    }, [isLoadedScript, onPayClick]);

    return (
        <ST.AppleBtnWrapper>
            <ST.AppleBtnGlobalStyle />
            {isLoadedScript &&
                // @ts-ignore
                <apple-pay-button
                    buttonstyle='black'
                    type='plain'
                    locale='ru-RU'
                />
            }
        </ST.AppleBtnWrapper>
    );
};
